import type { Format, Segment } from '../schemas/format';

export type ColumnState = 'past' | 'active' | 'future';
export type LayoutMode = 'fit' | 'focus';

/** Past / active / future for each column on a sheet, relative to the segment being flowed. */
export const columnStates = (format: Format, segmentIds: string[], activeSegmentId: string | null): ColumnState[] => {
  const order = new Map(format.segments.map((s, i) => [s.id, i]));
  const activeIdx = activeSegmentId === null ? -1 : (order.get(activeSegmentId) ?? -1);
  if (activeIdx < 0) return segmentIds.map(() => 'past');
  // If the active segment isn't on this sheet, the next one on the sheet is "active".
  let activeCol = segmentIds.findIndex((id) => (order.get(id) ?? 0) >= activeIdx);
  if (activeCol < 0) activeCol = segmentIds.length - 1;
  return segmentIds.map((_, i) => (i < activeCol ? 'past' : i === activeCol ? 'active' : 'future'));
};

export interface ColumnLayout {
  template: string;
  /** Columns shrunk to a sliver: show only the header label. */
  collapsed: boolean[];
}

/**
 * Grid layout for a sheet. The active speech gets ~3x the room; future speeches collapse to slivers;
 * questioning periods are narrower than speeches.
 * In focus mode only the active column and the column it's most likely answering stay open.
 * `withContent` (segment ids that have notes on this sheet) makes the "answering" guess smarter.
 */
export const columnLayout = (segments: Segment[], states: ColumnState[], mode: LayoutMode, activeCol: number, withContent?: Set<string>): ColumnLayout => {
  const answering = mode === 'focus' ? answeredColumn(segments, activeCol, withContent) : -1;
  const collapsed: boolean[] = [];
  const cols = segments.map((seg, i) => {
    const st = states[i];
    const q = seg.kind === 'questioning';
    let w: string;
    if (st === 'active') w = mode === 'focus' ? 'minmax(18rem, 4fr)' : `minmax(${q ? '12rem' : '16rem'}, ${q ? 2 : 3}fr)`;
    else if (st === 'future') w = '2.25rem';
    else if (mode === 'focus') w = i === answering ? 'minmax(12rem, 2fr)' : '2.25rem';
    // Readable minimums: when they don't all fit, the flow scrolls sideways instead of squashing notes.
    else w = q ? 'minmax(6rem, 0.6fr)' : 'minmax(9rem, 1fr)';
    collapsed.push(w === '2.25rem');
    return w;
  });
  return { template: cols.join(' '), collapsed };
};

export const columnTemplate = (segments: Segment[], states: ColumnState[], mode: LayoutMode, activeCol: number, withContent?: Set<string>): string =>
  columnLayout(segments, states, mode, activeCol, withContent).template;

/**
 * The column the active speech is most likely answering: the nearest earlier speech by the other side
 * that has notes; falling back to the sheet's first column (the case itself).
 */
export const answeredColumn = (segments: Segment[], activeCol: number, withContent?: Set<string>): number => {
  const active = segments[activeCol];
  if (!active) return -1;
  for (let i = activeCol - 1; i >= 0; i--) {
    const s = segments[i]!;
    const otherSide = active.by === 'both' || s.by !== active.by;
    if (s.kind === 'speech' && otherSide && (!withContent || withContent.has(s.id))) return i;
  }
  return activeCol > 0 ? 0 : -1;
};
